import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { RxCrossCircled } from "react-icons/rx"
import { useForm, Controller } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import EmailIcon from "../../../Utils/assets/img/icons/Emaillogo.png"
import smsIcon from "../../../Utils/assets/img/icons/SMS.png"
import "./Integrations.css"
import { BsArrowLeft } from "react-icons/bs"
import { Button } from "react-bootstrap"
import { GetRequest, PutRequest, PostRequest } from "../../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
// Import libphonenumber-js for validation
import { parsePhoneNumber } from "libphonenumber-js"
import { toast } from "sonner"

// Custom phone validation function
const validatePhoneNumber = (phone) => {
  try {
    if (!phone) return false

    // Parse the phone number
    const phoneNumber = parsePhoneNumber(`+${phone}`)

    if (!phoneNumber) return false

    // Check if the phone number is valid
    const isValid = phoneNumber.isValid()

    if (!isValid) return false

    // Get country-specific validation
    const country = phoneNumber.country
    const nationalNumber = phoneNumber.nationalNumber



    // Country-specific validation rules
    if (country === "IN") {
      // India: should have exactly 10 digits
      return nationalNumber.length === 10
    }

    if (country === "US") {
      // US: should have exactly 10 digits
      return nationalNumber.length === 10
    }

    // For other countries, use the library's default validation
    return isValid
  } catch (error) {
    console.error("Phone validation error:", error)
    return false
  }
}

// Different schemas for different services
const smsSchema = z.object({
  sms_apiKey: z.string().min(1, "SMS API Key is required"),
})

const whatsappSchema = z.object({
  integrated_number: z
    .string()
    .min(1, "Integrated Number is required")
    .refine(
      (phone) => {
        const isValid = validatePhoneNumber(phone)
        if (!isValid) {
          try {
            const phoneNumber = parsePhoneNumber(`+${phone}`)
            if (phoneNumber?.country === "IN") {
              return false // Will show India-specific error message
            }
          } catch (e) {
            // ignore
          }
        }
        return isValid
      },
      {
        message: "Please enter a valid phone number.",
      },
    ),
  whatsapp_apiKey: z.string().min(1, "WhatsApp API Key is required"),
})

const emailSchema = z.object({
  email_apiKey: z.string().min(1, "Email API Key is required"),
})

export const MSG = () => {
  const [selectedService, setSelectedService] = useState(null)
  const [existingData, setExistingData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [phoneValidationInfo, setPhoneValidationInfo] = useState(null)
  const navigate = useNavigate()

  // Extract tokens from session
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  })

  const customerId = customer?.customer_details?.customer_id
  const token = customer?.token?.authorization
  const rf = customer?.token?.authentication

  const headers = {
    authentication: rf,
    Authorization: token,
  }

  const extras = { customer, setCustomer, rf, navigate }

  const msgServices = [
    {
      name: "SMS",
      description: "Add your SMS API which you want to use to send SMS messages. You can add only one API at a time.",
      icon: smsIcon,
      endpoint: "fetchSmsKey",
      updateEndpoint: "updateSmsApiKey",
      addEndpoint: "addSmsApiKey",
      schema: smsSchema,
      fields: ["sms_apiKey"],
    },
    {
      name: "WhatsApp",
      description:
        "Add your WhatsApp API which you want to use to send WhatsApp messages. You can add only one API at a time.",
      icon: "https://img.icons8.com/color/96/whatsapp.png",
      endpoint: "fetchWhatsappKey",
      updateEndpoint: "updateWhatsappKey",
      addEndpoint: "addWhatsappKey",
      schema: whatsappSchema,
      fields: ["integrated_number", "whatsapp_apiKey"],
    },
    {
      name: "Email",
      description: "Add your Email API which you want to use to send emails. You can add only one API at a time.",
      icon: EmailIcon,
      endpoint: "fetchEmailKey",
      updateEndpoint: "updateEmailApiKey",
      addEndpoint: "addEmailApiKey",
      schema: emailSchema,
      fields: ["email_apiKey"],
    },
  ]

  const permission = customer.customer_details?.permission
  const user_type = Number(customer.customer_details?.user_type)
  const settingPermission = permission?.filter(c => c.module_name === "setting")
  const integrationPermission = {
    isCreate: user_type !== 1 ? Boolean(Number(settingPermission?.filter(c => c.feature_name === "integration")?.create)) : true,
  }

  // Get current schema based on selected service
  const getCurrentSchema = () => {
    if (!selectedService) return smsSchema

    return selectedService.schema
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    trigger,
  } = useForm({
    resolver: zodResolver(getCurrentSchema()),
  })


  const fetchServiceData = async (service) => {
    setIsLoading(true)
    try {
      const res = await GetRequest(`/integration/${service.endpoint}/${customerId}`, { headers }, extras)

      if (res.status === "success" && res.data) {
        setExistingData(res.data)

        // Populate form with existing data based on service type
        if (service.name === "SMS") {
          if (res.data[0].sms_apiKey) {
            setValue("sms_apiKey", res.data[0].sms_apiKey)
          }
        } else if (service.name === "WhatsApp") {
          if (res.data[0].integrated_number) {
            // Set the phone number value for PhoneInput
            setValue("integrated_number", res.data[0].integrated_number)
            // Validate the existing phone number
            handlePhoneValidation(res.data[0].integrated_number)
          }
          if (res.data[0].whatsapp_apiKey) {
            setValue("whatsapp_apiKey", res.data[0].whatsapp_apiKey)
          }
        } else if (service.name === "Email") {
          if (res.data[0].email_apiKey) {
            setValue("email_apiKey", res.data[0].email_apiKey)
          }
        }
        return { hasData: true, data: res.data }
      } else {
        setExistingData(null)
        reset() // Clear form
        return { hasData: false, data: null }
      }
    } catch (err) {
      console.error("Error fetching service data:", err)
      setExistingData(null)
      reset()
      return { hasData: false, data: null }
    } finally {
      setIsLoading(false)
    }
  }

  const handleCardClick = async (service) => {
    setSelectedService(service)
    setPhoneValidationInfo(null)
    // Reset form with new schema
    reset()
    await fetchServiceData(service)
  }

  const handleCloseModal = () => {
    setSelectedService(null)
    setExistingData(null)
    setPhoneValidationInfo(null)
    reset()
  }

  // Handle phone number validation and info display
  const handlePhoneValidation = (phone) => {
    try {
      if (!phone) {
        setPhoneValidationInfo(null)
        return
      }

      const phoneNumber = parsePhoneNumber(`+${phone}`)

      if (phoneNumber) {
        const validationInfo = {
          country: phoneNumber.country,
          countryName:
            phoneNumber.country === "IN"
              ? "India"
              : phoneNumber.country === "US"
                ? "United States"
                : phoneNumber.country || "Unknown",
          nationalNumber: phoneNumber.nationalNumber,
          countryCallingCode: phoneNumber.countryCallingCode,
          isValid: phoneNumber.isValid(),
          formattedInternational: phoneNumber.formatInternational(),
          formattedNational: phoneNumber.formatNational(),
        }

        // Country-specific validation
        if (phoneNumber.country === "IN") {
          validationInfo.isValidForCountry = phoneNumber.nationalNumber.length === 10
          validationInfo.expectedLength = 10
          validationInfo.actualLength = phoneNumber.nationalNumber.length
        } else if (phoneNumber.country === "US") {
          validationInfo.isValidForCountry = phoneNumber.nationalNumber.length === 10
          validationInfo.expectedLength = 10
          validationInfo.actualLength = phoneNumber.nationalNumber.length
        } else {
          validationInfo.isValidForCountry = phoneNumber.isValid()
        }

        setPhoneValidationInfo(validationInfo)
      } else {
        setPhoneValidationInfo(null)
      }
    } catch (error) {
      console.error("Phone validation error:", error)
      setPhoneValidationInfo(null)
    }
  }

  const onSubmit = async (data) => {

    // For WhatsApp, log the phone number details with validation
    if (selectedService?.name === "WhatsApp" && data.integrated_number) {
      try {
        const phoneNumber = parsePhoneNumber(`+${data.integrated_number}`)

        if (phoneNumber) {

        }
      } catch (error) {
        console.error("Error parsing phone number:", error)
      }
    }

    if (!selectedService) return

    setIsLoading(true)
    try {
      let res
      if (existingData) {
        // Update existing data
        let updatePayload = {}
        let updateEndpoint = ""

        if (selectedService.name === "SMS") {
          updatePayload = {
            sms_apiKey: data.sms_apiKey,
          }
          updateEndpoint = `/integration/updateSmsApiKey/${customerId}/${existingData[0]?.id || existingData[0]?.id}`
        } else if (selectedService.name === "WhatsApp") {
          updatePayload = {
            integrated_number: data.integrated_number,
            whatsapp_apiKey: data.whatsapp_apiKey,
            key_id: existingData[0]?.id,
            customer_id: customerId,
          }
          updateEndpoint = `/integration/updateWhatsappKey/${customerId}/${existingData[0]?.whatsapp_id || existingData[0]?.id}`
        } else if (selectedService.name === "Email") {
          updatePayload = {
            email_apiKey: data.email_apiKey,
          }
          updateEndpoint = `/integration/updateEmailApiKey/${customerId}/${existingData[0]?.email_id || existingData[0]?.id}`
        }

        res = await PutRequest(updateEndpoint, updatePayload, { headers }, extras)
      } else {
        // Add new data
        let addPayload = {}
        let addEndpoint = ""

        if (selectedService.name === "SMS") {
          addPayload = {
            sms_apiKey: data.sms_apiKey,
            customer_id: customerId,
          }
          addEndpoint = `/integration/${selectedService.addEndpoint}/${customerId}`
        } else if (selectedService.name === "WhatsApp") {
          addPayload = {
            integrated_number: data.integrated_number,
            whatsapp_apiKey: data.whatsapp_apiKey,
            customer_id: customerId,
            user_login_id: "11",
          }
          addEndpoint = `/integration/${selectedService.addEndpoint}/${customerId}`
        } else if (selectedService.name === "Email") {
          addPayload = {
            email_apiKey: data.email_apiKey,
            customer_id: customerId,
          }
          addEndpoint = `/integration/${selectedService.addEndpoint}/${customerId}`
        }

        res = await PostRequest(addEndpoint, addPayload, false, { headers }, extras)
      }

      if (res.status === "success") {
        alert(`${selectedService.name} API Key ${existingData ? "updated" : "saved"} successfully!`)
        handleCloseModal()
      } else {
        alert(`Failed to ${existingData ? "update" : "save"} ${selectedService.name} API Key`)
      }
    } catch (error) {
      console.error("Error submitting form:", error)
      alert(`Error ${existingData ? "updating" : "saving"} ${selectedService.name} API Key`)
    } finally {
      setIsLoading(false)
    }
  }

  // Render form fields based on selected service
  const renderFormFields = () => {
    if (!selectedService) return null

    if (selectedService.name === "SMS") {
      return (
        <div className="mb-3">
          <label htmlFor="sms_apiKey" className="form-label">
            SMS API Key <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="sms_apiKey"
            className={`form-control ${errors.sms_apiKey ? "is-invalid" : ""}`}
            placeholder="Enter your SMS API Key"
            {...register("sms_apiKey")}
          />
          {errors.sms_apiKey && <div className="invalid-feedback">{errors.sms_apiKey.message}</div>}
        </div>
      )
    }

    if (selectedService.name === "WhatsApp") {
      return (
        <>
          <div className="mb-3">
            <label htmlFor="integrated_number" className="form-label">
              Integrated Number <span className="text-danger">*</span>
            </label>
            <Controller
              name="integrated_number"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PhoneInput
                  country={"in"} // Default to India
                  value={value}
                  onChange={(phone) => {

                    onChange(phone)
                    handlePhoneValidation(phone)
                    // Trigger validation
                    setTimeout(() => trigger("integrated_number"), 100)
                  }}
                  placeholder="Enter phone number"
                  inputClass={`form-control ${errors.integrated_number ? "is-invalid" : ""}`}
                  containerClass="phone-input-container"
                  buttonClass="phone-input-button"
                  dropdownClass="phone-input-dropdown"
                  searchClass="phone-input-search"
                  inputStyle={{
                    width: "100%",
                    height: "38px",
                    fontSize: "14px",
                    paddingLeft: "48px",
                  }}
                  buttonStyle={{
                    border: "1px solid #ced4da",
                    borderRight: "none",
                    backgroundColor: "#f8f9fa",
                  }}
                  containerStyle={{
                    width: "100%",
                  }}
                  // Prefer certain countries
                  preferredCountries={["in", "us", "gb", "ca"]}
                  // Enable search
                  enableSearch={true}
                  searchPlaceholder="Search countries..."
                />
              )}
            />
            {errors.integrated_number && (
              <div className="invalid-feedback d-block">{errors.integrated_number.message}</div>
            )}


          </div>
          <div className="mb-3">
            <label htmlFor="whatsapp_apiKey" className="form-label">
              WhatsApp API Key <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              id="whatsapp_apiKey"
              className={`form-control ${errors.whatsapp_apiKey ? "is-invalid" : ""}`}
              placeholder="Enter your WhatsApp API Key"
              {...register("whatsapp_apiKey")}
            />
            {errors.whatsapp_apiKey && <div className="invalid-feedback">{errors.whatsapp_apiKey.message}</div>}
          </div>
        </>
      )
    }

    if (selectedService.name === "Email") {
      return (
        <div className="mb-3">
          <label htmlFor="email_apiKey" className="form-label">
            Email API Key <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="email_apiKey"
            className={`form-control ${errors.email_apiKey ? "is-invalid" : ""}`}
            placeholder="Enter your Email API Key"
            {...register("email_apiKey")}
          />
          {errors.email_apiKey && <div className="invalid-feedback">{errors.email_apiKey.message}</div>}
        </div>
      )
    }

    return null
  }

  return (
    <>
      <div className="mb-5" style={{ marginLeft: "6px" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <div className="d-flex flex-column text-start">
            <h2 className="h4 mb-2">MSG91</h2>
            <span className="text-muted mb-1">
              You can manage Logo, Receipt, Admission, etc. information LMS system from here.
            </span>
          </div>
          <Button className="btn btn-default-outline" onClick={() => navigate(-1)}>
            <BsArrowLeft className="me-1 " /> Back
          </Button>
        </div>
        <hr className="text-muted" />
        <div className="row gx-3 gy-3 justify-content-start ms-1">
          {msgServices.map((item, index) => (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 d-flex" key={index} style={{ maxWidth: "260px" }}>
              <div
                className="card text-center border-0 shadow-sm custom-card-integration w-100"
                onClick={() => {
                  if (!integrationPermission?.isCreate) {
                    toast.warning("You are not authorized for this action!")
                    return
                  }
                  handleCardClick(item)
                }}
                style={{
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  opacity: isLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
              >
                <div className="card-body p-4 d-flex flex-column align-items-center">
                  <img
                    src={item.icon || "/placeholder.svg"}
                    alt={item.name}
                    className="card-logo-integration mb-3"
                    style={{ height: "32px" }}
                  />
                  <h6 className="text-truncate w-100 small fw-semibold mb-2">{item.name}</h6>
                  <div className="tooltip-integration w-100">
                    <p className="card-description text-muted small m-0 text-truncate-2">{item.description}</p>
                    <div className="tooltip-box-integration">{item.description}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Popup */}
      {selectedService && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={handleCloseModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <div className="d-flex align-items-start justify-content-between w-100">
                  {/* Left: Icon + Title + Description */}
                  <div className="d-flex">
                    <img
                      src={selectedService.icon || "/placeholder.svg"}
                      alt={selectedService.name}
                      style={{ height: "32px", width: "32px", marginRight: "12px" }}
                    />
                    <div className="d-flex flex-column">
                      <h5 className="modal-title mt-1 mb-1">
                        {existingData ? `Update ${selectedService.name} Api` : `Add ${selectedService.name} Api`}
                      </h5>
                      <p className="text-muted mb-0" style={{ opacity: "0.4" }}>
                        {selectedService.description}
                      </p>
                    </div>
                  </div>
                  {/* Right: Close Button */}
                  <button
                    type="button"
                    className="btn"
                    onClick={handleCloseModal}
                    aria-label="Close"
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.8rem",
                      cursor: "pointer",
                      color: "#888",
                      marginTop: "-20px",
                      marginRight: "0px",
                      opacity: "0.4",
                    }}
                  >
                    <RxCrossCircled />
                  </button>
                </div>
              </div>
              <div className="modal-body">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit(onSubmit)}>
                    {renderFormFields()}
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn"
                        onClick={handleCloseModal}
                        style={{ border: "1px solid black" }}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn"
                        style={{
                          backgroundColor: "rgb(122,58,94)",
                          color: "white",
                        }}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            {existingData ? "Updating..." : "Saving..."}
                          </>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
